// Remontee des erreurs d'execution vers GlitchTip (compatible Sentry), pilotee uniquement par
// l'environnement : sans DSN, `optionsSentry` rend null et rien n'est initialise. Ce module est
// pur (aucun import, utilisable dans le navigateur comme dans Node) : il porte les options
// communes et le nettoyage des evenements avant envoi. Les couches minces qui appellent le SDK
// vivent a cote (erreurs_sentry_node.ts, front-spa/src/lib/erreurs_sentry.ts) et ne sont pas
// testees ; tout ce qui se decide est ici.

export type EnvSentry = {
  dsn?: string | null;
  environnement?: string | null;
  release?: string | null;
};

export type RequeteSentry = {
  url?: string;
  method?: string;
  data?: unknown;
  query_string?: unknown;
  cookies?: Record<string, string>;
  headers?: Record<string, string>;
};

export type FilAriane = {
  type?: string;
  category?: string;
  message?: string;
  data?: Record<string, unknown>;
};

export type UtilisateurSentry = {
  id?: string | number;
  email?: string;
  ip_address?: string | null;
  username?: string;
} & Record<string, unknown>;

// Sous-ensemble structurel de l'Event de Sentry : tout est optionnel, le type reel du SDK s'y
// assigne sans rien importer de @sentry/core.
export type EvenementSentry = {
  message?: string;
  logentry?: { message?: string; params?: unknown[] };
  request?: RequeteSentry;
  extra?: Record<string, unknown>;
  contexts?: Record<string, unknown>;
  breadcrumbs?: FilAriane[];
  user?: UtilisateurSentry;
  exception?: { values?: Array<{ type?: string; value?: string }> };
  tags?: Record<string, unknown>;
};

export type OptionsSentry = {
  dsn: string;
  environment: string;
  release?: string;
  sendDefaultPii: false;
  tracesSampleRate: 0;
  beforeSend: typeof nettoyerEvenement;
  beforeBreadcrumb: typeof nettoyerFilAriane;
};

export const LONGUEUR_MAX_CHAINE = 500;
export const RETIRE = "[retire]";
const MARQUE_TRONQUE = "[tronque]";
const PROFONDEUR_MAX = 8;

// Mots qui designent un contenu ou un secret : toute cle qui les porte (au singulier ou au
// pluriel, en camelCase ou en snake_case) est remplacee par RETIRE dans extra et contexts.
const MOTS_SENSIBLES = new Set([
  "content",
  "message",
  "text",
  "prompt",
  "instruction",
  "email",
  "password",
  "token",
  "secret",
  "apikey",
  "authorization",
  "cookie",
  "body",
]);

// En-tetes de requete jamais envoyes.
const ENTETES_RETIRES = new Set([
  "authorization",
  "proxyauthorization",
  "cookie",
  "setcookie",
  "xapikey",
]);

/** Les options communes aux SDK Node et navigateur, ou null quand aucun DSN n'est pose. */
export function optionsSentry(env: EnvSentry): OptionsSentry | null {
  const dsn = env.dsn?.trim();
  if (!dsn) {
    return null;
  }
  const release = env.release?.trim();
  return {
    dsn,
    environment: env.environnement?.trim() || "production",
    ...(release ? { release } : {}),
    sendDefaultPii: false,
    tracesSampleRate: 0,
    beforeSend: nettoyerEvenement,
    beforeBreadcrumb: nettoyerFilAriane,
  };
}

function mots(cle: string): string[] {
  return cle
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map((m) => m.replace(/s$/, ""));
}

function cleSensible(cle: string): boolean {
  const m = mots(cle);
  return (
    m.some((mot) => MOTS_SENSIBLES.has(mot)) || MOTS_SENSIBLES.has(m.join(""))
  );
}

function enteteRetire(nom: string): boolean {
  const n = nom.toLowerCase().replace(/[^a-z0-9]/g, "");
  return ENTETES_RETIRES.has(n) || n.includes("token") || n.includes("secret");
}

function tronquer(s: string): string {
  return s.length > LONGUEUR_MAX_CHAINE
    ? `${s.slice(0, LONGUEUR_MAX_CHAINE)}${MARQUE_TRONQUE}`
    : s;
}

function sansRequete(url: string): string {
  const i = url.search(/[?#]/);
  return i === -1 ? url : url.slice(0, i);
}

// Copie nettoyee d'une valeur : cles sensibles retirees, chaines tronquees, profondeur bornee,
// cycles coupes. L'objet d'origine n'est jamais modifie.
function nettoyerValeur(
  valeur: unknown,
  profondeur: number,
  chemin: Set<object>
): unknown {
  if (typeof valeur === "string") {
    return tronquer(valeur);
  }
  if (valeur === null || typeof valeur !== "object") {
    return valeur;
  }
  if (profondeur >= PROFONDEUR_MAX) {
    return "[profondeur]";
  }
  if (chemin.has(valeur)) {
    return "[cycle]";
  }
  chemin.add(valeur);
  try {
    if (Array.isArray(valeur)) {
      return valeur.map((v) => nettoyerValeur(v, profondeur + 1, chemin));
    }
    const sortie: Record<string, unknown> = {};
    for (const [cle, v] of Object.entries(valeur)) {
      sortie[cle] = cleSensible(cle)
        ? RETIRE
        : nettoyerValeur(v, profondeur + 1, chemin);
    }
    return sortie;
  } finally {
    chemin.delete(valeur);
  }
}

function nettoyerObjet(
  objet: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  return objet
    ? (nettoyerValeur(objet, 0, new Set()) as Record<string, unknown>)
    : undefined;
}

function nettoyerRequete(requete: RequeteSentry): RequeteSentry {
  const {
    data: _corps,
    cookies: _cookies,
    query_string: _requete,
    url,
    headers,
    ...reste
  } = requete;
  const sortie: RequeteSentry = { ...reste };
  if (url) {
    sortie.url = sansRequete(url);
  }
  if (headers) {
    sortie.headers = Object.fromEntries(
      Object.entries(headers).filter(([nom]) => !enteteRetire(nom))
    );
  }
  return sortie;
}

function nettoyerUtilisateur(user: UtilisateurSentry): UtilisateurSentry {
  const { email: _email, ip_address: _ip, username: _nom, ...reste } = user;
  return reste;
}

/**
 * `beforeBreadcrumb` : une requete xhr/fetch qui portait un corps est supprimee ; les autres
 * perdent leur chaine de requete et leurs cles sensibles.
 */
export function nettoyerFilAriane<F extends FilAriane>(fil: F): F | null {
  const donnees = fil.data;
  const estRequete =
    fil.type === "http" || fil.category === "xhr" || fil.category === "fetch";
  if (estRequete && donnees && aUnCorps(donnees)) {
    return null;
  }
  const sortie: F = { ...fil };
  if (typeof fil.message === "string") {
    sortie.message = tronquer(fil.message);
  }
  if (donnees) {
    const propres = nettoyerObjet(donnees) ?? {};
    for (const cle of ["url", "from", "to"]) {
      if (estRequete && cle === "url" && typeof propres[cle] === "string") {
        propres[cle] = sansRequete(propres[cle] as string);
      }
    }
    sortie.data = propres;
  }
  return sortie;
}

function aUnCorps(donnees: Record<string, unknown>): boolean {
  const taille = donnees.request_body_size;
  if (typeof taille === "number" && taille > 0) {
    return true;
  }
  return ["body", "input", "payload"].some((cle) => cle in donnees);
}

/**
 * `beforeSend` : retire corps, cookies et en-tetes sensibles de la requete, les cles de contenu
 * ou de secret de extra/contexts, l'e-mail et l'IP du membre ; tronque toute chaine longue.
 * Rend une copie ; l'evenement d'origine n'est pas modifie.
 */
export function nettoyerEvenement<E extends EvenementSentry>(event: E): E {
  const sortie: E = { ...event };
  if (typeof event.message === "string") {
    sortie.message = tronquer(event.message);
  }
  if (event.logentry) {
    sortie.logentry = {
      ...event.logentry,
      ...(typeof event.logentry.message === "string"
        ? { message: tronquer(event.logentry.message) }
        : {}),
    };
  }
  if (event.request) {
    sortie.request = nettoyerRequete(event.request);
  }
  if (event.extra) {
    sortie.extra = nettoyerObjet(event.extra);
  }
  if (event.contexts) {
    sortie.contexts = nettoyerObjet(event.contexts);
  }
  if (event.user) {
    sortie.user = nettoyerUtilisateur(event.user);
  }
  if (event.exception?.values) {
    sortie.exception = {
      ...event.exception,
      values: event.exception.values.map((v) =>
        typeof v.value === "string" ? { ...v, value: tronquer(v.value) } : v
      ),
    };
  }
  if (event.breadcrumbs) {
    sortie.breadcrumbs = event.breadcrumbs
      .map((fil) => nettoyerFilAriane(fil))
      .filter((fil): fil is FilAriane => fil !== null);
  }
  return sortie;
}

export type IdentiteSentry = { utilisateur?: string; workspace?: string };

/** Les deux sId (membre, workspace) d'un Authenticator, par typage structurel, sans jamais jeter. */
export function identiteDepuisAuth(auth: unknown): IdentiteSentry {
  const a = auth as
    | {
        user?: () => { sId?: string } | null | undefined;
        workspace?: () => { sId?: string } | null | undefined;
      }
    | null
    | undefined;
  const identite: IdentiteSentry = {};
  try {
    const sId = typeof a?.user === "function" ? a.user()?.sId : undefined;
    if (sId) {
      identite.utilisateur = sId;
    }
  } catch {
    // un Authenticator sans session jette : l'identite reste vide
  }
  try {
    const sId =
      typeof a?.workspace === "function" ? a.workspace()?.sId : undefined;
    if (sId) {
      identite.workspace = sId;
    }
  } catch {
    // idem
  }
  return identite;
}

/** Bruits que les gardes du processus ignorent deja : ne pas les remonter non plus. */
export function estErreurBenigne(raison: unknown): boolean {
  if (!(raison instanceof Error)) {
    return false;
  }
  if (raison.name === "CancelledFailure") {
    return true;
  }
  return (
    "code" in raison &&
    (raison as { code?: unknown }).code === "ERR_STREAM_UNABLE_TO_PIPE"
  );
}

export type TentativeActivite = {
  attempt: number;
  /** `maximumAttempts` de la politique de reessai ; 0, null ou absent = illimite ou inconnu. */
  maximumAttempts?: number | null;
};

// Type de l'ApplicationFailure que run_model jette pour qu'une erreur de modele reessayable (rate
// limit, surcharge, 5xx, timeout) soit rejouee par Temporal ; le prefixe est pose dans
// front/temporal/agent_loop/lib/run_model_errors.ts (makeRunModelLLMError).
const PREFIXE_ERREUR_MODELE_REESSAYABLE = "RunModelLLMError:";

/**
 * Vrai quand l'echec d'une activite Temporal va etre reessaye (24/10) : une tentative intermediaire
 * ne vaut pas un evenement GlitchTip, la suivante peut reussir ; elle reste dans le journal. Vaut
 * un evenement : la derniere tentative permise par la politique, ou une ApplicationFailure
 * `nonRetryable`. Sans `maximumAttempts` connu (politique illimitee, vieux serveur), chaque
 * tentative est remontee, SAUF l'erreur de modele reessayable de run_model : elle n'est jetee que
 * quand un reessai suit (shouldSurfaceModelError), son echec final est publie en agent_error et
 * remonte par signalerErreurAgent.
 */
export function estTentativeIntermediaire(
  erreur: unknown,
  tentative: TentativeActivite
): boolean {
  const echec =
    erreur instanceof Error
      ? (erreur as Error & { type?: unknown; nonRetryable?: unknown })
      : null;
  const max = tentative.maximumAttempts;
  const bornee = typeof max === "number" && max > 0;
  if (echec?.nonRetryable === true || (bornee && tentative.attempt >= max)) {
    return false;
  }
  if (
    typeof echec?.type === "string" &&
    echec.type.startsWith(PREFIXE_ERREUR_MODELE_REESSAYABLE)
  ) {
    return true;
  }
  return bornee;
}

export type Rapporteur = (
  erreur: unknown,
  contexte?: Record<string, string>
) => void;

let rapporteur: Rapporteur | null = null;

/** Pose (ou retire) la fonction qui remonte une erreur attrapee par un composant. */
export function definirRapporteur(fn: Rapporteur | null): void {
  rapporteur = fn;
}

/**
 * Remonte une erreur attrapee (ErrorBoundary, errorElement du routeur). Sans rapporteur, ne fait
 * rien ; une exception du rapporteur est avalee : la remontee ne casse jamais la page.
 */
export function rapporterErreur(
  erreur: unknown,
  contexte?: Record<string, string>
): boolean {
  if (!rapporteur) {
    return false;
  }
  try {
    rapporteur(erreur, contexte);
    return true;
  } catch {
    return false;
  }
}
